// Open roles shown on /careers and rendered in full at /careers/<slug>.
// To post a role, add an entry to `postings` with `published: true` and
// deploy. To close it, set `published: false` (the entry stays as a template
// for the next one) or remove it. There is no CMS on purpose: at a few hires
// a year a code review is the approval step.
//
// BC's Pay Transparency Act requires an expected pay range on every publicly
// advertised posting, so payRange is mandatory and tested.

export type EmploymentType = 'Full-time' | 'Part-time' | 'Contract' | 'Co-op';

export interface JobPosting {
  /** Only published postings appear on the site. Unpublished ones 404. */
  published: boolean;
  /** URL-safe identifier: the page path, the apply link, and the email subject. */
  slug: string;
  title: string;
  /** For example 'Vancouver, BC (hybrid)'. Shown on the card and the page. */
  location: string;
  type: EmploymentType;
  /** For example 'CAD 95,000 to 120,000 per year'. Shown on the card and the page. */
  payRange: string;
  /** One sentence for the card on /careers. */
  summary: string;
  /** Who the role reports to, shown in the page header. */
  reportsTo?: string;
  /** Extra detail about where and how the work happens, shown under the location. */
  locationNote?: string;
  /** Opening paragraphs of the full description. */
  about: string[];
  /** "What you will do" bullets. */
  responsibilities: string[];
  /** "What you may also do" bullets. */
  alsoMayDo?: string[];
  /** "Required" bullets. */
  required: string[];
  /** "Nice to have" bullets. */
  niceToHave?: string[];
  /** ISO date, YYYY-MM-DD. */
  postedOn: string;
}

/** Reserved slug for the open-ended application. Postings may not use it. */
export const OPEN_APPLICATION_SLUG = 'open';

/** Every posting, published or not. Tests run over this list. */
export const postings: JobPosting[] = [
  {
    published: false,
    slug: 'field-operations-lead',
    title: 'Field Operations Lead',
    location: 'Fraser Valley, BC',
    locationNote: 'A daily on-site role at customer farms across the region.',
    type: 'Full-time',
    payRange: 'CAD 85,000 to 100,000 per year plus equity',
    reportsTo: 'CTO',
    summary:
      'Own how our systems get installed, commissioned, and kept running in the field.',
    about: [
      'Ensemble Scientific builds smart irrigation and water management hardware for growers in the Fraser Valley. We are looking for a Field Operations Lead to own how our systems get installed, commissioned, and kept running in the field.',
      'This is the person growers deal with when something is not working, and the person who decides how field work gets done. You will own the field function end to end: scheduling, installation quality, diagnosis, and the standards and kit that make repeat visits unnecessary.',
    ],
    responsibilities: [
      'Own installation and commissioning of PulseLogic and RadSum systems at customer sites, from site assessment through verified handover.',
      'Act as primary responder for field issues, and diagnose faults across irrigation infrastructure, electrical systems, valves, pumps, sensors, and our own equipment.',
      'Distinguish problems in Ensemble equipment from problems in third-party or site infrastructure, and resolve or escalate accordingly.',
      'Be the day-to-day technical contact for growers, contractors, and installers.',
      'Own the field kit: tooling, diagnostic equipment, spares, consumables, and readiness before every visit.',
      'Define and document installation and troubleshooting procedures so field work is repeatable rather than improvised.',
      'Feed field findings back to engineering: recurring failures, install pain points, and gaps in our tooling and diagnostics.',
      'Plan field work around the irrigation season and coordinate scheduling with customers.',
    ],
    alsoMayDo: [
      'Contribute to hardware and firmware improvements based on what you see in the field.',
      'Help scope and later build out field capacity as we grow.',
    ],
    required: [
      'Diploma or degree in electrical, electronics, mechatronics, or a related technical field, or equivalent hands-on experience.',
      '3 or more years diagnosing and repairing electrical or electromechanical systems in the field.',
      'Confident with a multimeter and standard electrical troubleshooting on low-voltage DC and single-phase AC.',
      'Competent with hand and power tools, including drilling, cutting, fastening, conduit, and cable work.',
      'Comfortable with physical work outdoors on farms: lifting and carrying equipment, working in variable weather, and moving around wet or uneven ground.',
      'Able to explain technical problems clearly to non-technical customers.',
      'Based in or willing to work daily across the Fraser Valley, with a valid driver’s licence.',
    ],
    niceToHave: [
      'Irrigation, pump, or agricultural equipment experience.',
      'Familiarity with cellular or wireless field devices, solar power systems, or industrial sensors.',
      'Basic scripting or comfort with a technical dashboard and device logs.',
      'Experience setting up service processes or documentation from scratch.',
    ],
    postedOn: '2026-09-25',
  },
];

/** The postings the site shows. */
export const jobs: JobPosting[] = postings.filter((job) => job.published);

export function findJob(slug: string): JobPosting | undefined {
  return jobs.find((job) => job.slug === slug);
}

export function roleLabel(slug: string): string {
  return findJob(slug)?.title ?? 'Open application';
}
